import { useState, useCallback } from 'react'
import { ExternalAssignee } from '@/react/types/observations'

interface UseEmailManagementProps {
  existingAssignees: ExternalAssignee[]
}

interface UseEmailManagementReturn {
  email: string
  setEmail: (email: string) => void
  error: string
  setError: (error: string) => void
  pendingEmails: string[]
  validateEmail: (email: string) => boolean
  checkDuplicateEmail: (email: string) => boolean
  handleEmailConfirm: (emailValue: string) => boolean
  handleRemovePendingEmail: (emailToRemove: string) => void
  clearEmail: () => void
  resetEmailState: () => void
}

export const useEmailManagement = ({
  existingAssignees,
}: UseEmailManagementProps): UseEmailManagementReturn => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [pendingEmails, setPendingEmails] = useState<string[]>([])

  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }, [])

  const checkDuplicateEmail = useCallback(
    (emailValue: string): boolean => {
      const emailExists =
        pendingEmails.includes(emailValue) ||
        existingAssignees.some(
          (assignee) =>
            assignee.external_contact?.email?.toLowerCase() ===
              emailValue.toLowerCase() && !assignee.deleted,
        )
      return emailExists
    },
    [pendingEmails, existingAssignees],
  )

  const handleEmailConfirm = useCallback(
    (emailValue: string): boolean => {
      setError('')

      // Basic email validation
      if (!validateEmail(emailValue)) {
        setError('Please enter a valid email address')
        return false
      }

      // Check for duplicates
      if (checkDuplicateEmail(emailValue)) {
        setError('This email is already in the list')
        return false
      }

      // Add to pending list
      setPendingEmails((prev) => [...prev, emailValue])
      setEmail('') // Clear input
      return true
    },
    [validateEmail, checkDuplicateEmail],
  )

  const handleRemovePendingEmail = useCallback((emailToRemove: string) => {
    setPendingEmails((prev) => prev.filter((email) => email !== emailToRemove))
  }, [])

  const clearEmail = useCallback(() => {
    setEmail('')
    setError('')
  }, [])

  const resetEmailState = useCallback(() => {
    setEmail('')
    setError('')
    setPendingEmails([])
  }, [])

  return {
    email,
    setEmail,
    error,
    setError,
    pendingEmails,
    validateEmail,
    checkDuplicateEmail,
    handleEmailConfirm,
    handleRemovePendingEmail,
    clearEmail,
    resetEmailState,
  }
}
